import React from 'react'
// libs
import { FieldArray, arrayPush, formValueSelector } from 'redux-form'
import { useDispatch, batch, useSelector } from 'react-redux'
import styled from 'styled-components'
// components
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Typography,
  Accordion as AccordionOrigin,
  AccordionDetails as AccordionDetails,
  AccordionSummary as AccordionSummary
} from '@mui/material'
import ResultRow from './ResultRow'
// constants
import { Forms, RECURRENCE_ENDS_VALUES, REPEAT_VALUES } from 'constants/index'
// context
import { KPIContext } from 'context/index'
// themes
import { colors } from 'themes/index'
// utils
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { darken } from 'polished'
import { generateRanges, getLatestPeriod, formatPeriod } from 'utils/index'

// styled
const Accordion = styled(AccordionOrigin)`
  && {
    margin: 8px 0;
    border-radius: 4px;
    box-shadow:
      0 1px 3px 0 rgba(0, 0, 0, 0.2),
      0 1px 1px 0 rgba(0, 0, 0, 0.14),
      0 0 0 0 rgba(0, 0, 0, 0.12);

    &:before {
      display: none;
    }
  }
`

const Wrapper = styled('div')`
  margin-bottom: 24px;
`

const ShowMoreButton = styled('div')`
  color: ${colors.blue500};
  cursor: pointer;
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  width: 100%;

  &:hover {
    color: ${darken(0.1, colors.blue500)};
  }
`

const SingleRow = ({ field, current, targetForm }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const { startPoint, lastDayOfThePeriod } = current

  return React.useMemo(
    () => (
      <Accordion expanded={isOpen} onChange={(_, expanded) => setIsOpen(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>
          {startPoint && lastDayOfThePeriod && (
            <Typography style={{ color: colors.blue500, fontSize: 16, fontWeight: isOpen ? 600 : 400 }}>
              Period {moment(startPoint).format('D MMMM Y')} to {moment(lastDayOfThePeriod).format('D MMMM Y')}
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails style={{ display: 'flex', flexDirection: 'column', padding: '8px 24px' }}>
          <FieldArray name={`${field}.results`} component={ResultRow} targetForm={targetForm} />
        </AccordionDetails>
      </Accordion>
    ),
    [isOpen]
  )
}

const PeriodRow = ({ fields, targetForm }) => {
  const dispatch = useDispatch()

  const { data } = useSelector((state) => ({
    data: formValueSelector(targetForm)(state, 'recurrence')
  }))

  const { entity } = React.useContext(KPIContext)

  const recurrence = get(entity, 'recurrence') || data

  const isFieldsAbsent = fields.getAll().filter(({ withStoredData }) => !withStoredData).length === 0

  const latest = getLatestPeriod(fields, recurrence)

  let isBeforeFuture = false

  if (get(recurrence, 'recurrenceEnds') === RECURRENCE_ENDS_VALUES.ON && latest) {
    const repeatEvery = parseInt(recurrence.repeatEveryValue)

    const period = recurrence.repeatEveryType === REPEAT_VALUES.MONTHLY ? 'month' : 'week'

    isBeforeFuture = moment(latest.lastDayOfThePeriod)
      .add(repeatEvery, period)
      .isBefore(moment(recurrence.recurrenceEndsAt), 'day')
  }

  const { ranges } = generateRanges({
    recurrence,
    nextStartPoint: get(latest, 'nextStartPoint')
  })

  const newPeriods = ranges.filter((range) =>
    fields
      .getAll()
      .map(formatPeriod)
      .every((storedRange) => !isEqual(storedRange, formatPeriod(range)))
  )

  const shouldShowMore =
    !isEmpty(recurrence) &&
    !isEmpty(newPeriods) &&
    (isFieldsAbsent || isBeforeFuture || recurrence.recurrenceEnds === RECURRENCE_ENDS_VALUES.NEVER || !latest)

  const handleShowMoreClick = () => {
    batch(() => {
      newPeriods.forEach((value) => {
        dispatch(
          arrayPush(Forms.KPI_RESULT_TARGETS_FORM, 'differentResults', {
            ...value,
            results: []
          })
        )
      })
    })
  }

  const comparePeriods = (a, b) =>
    moment(a.item.startPoint).unix() >= moment(b.item.startPoint).unix() &&
    moment(a.item.lastDayOfThePeriod).unix() >= moment(b.item.lastDayOfThePeriod).unix()
      ? 1
      : -1

  React.useEffect(() => {
    if (isFieldsAbsent) {
      handleShowMoreClick()
    }
  }, [])

  return (
    <>
      <Wrapper>
        {fields
          .map((fieldName, index) => ({ fieldName, index, item: fields.get(index) }))
          .sort(comparePeriods)
          .map((field) => {
            return (
              <SingleRow field={field.fieldName} key={field.item.id} current={field.item} targetForm={targetForm} />
            )
          })}
      </Wrapper>

      {shouldShowMore && <ShowMoreButton onClick={handleShowMoreClick}>Show more...</ShowMoreButton>}
    </>
  )
}

export default PeriodRow
